import type {
  GetChangeEventsParams,
  GetPerformanceParams,
  GetStrategiesParams,
} from '@/repository/dashboard';
import type {
  ListKpisParams,
  ListStrategiesParams,
  StrategyTrendParams,
} from '@/repository/strategies';
import type { EligibleGoalShape } from '@/repository/conversion-goals';
import type { ListCreditLedgerParams } from '@/repository/billing';
import type { AiPerformanceParams } from '@/repository/ai-performance';
import type { ListAudiencesParams } from '@/repository/audiences';
import type { BusinessModel } from '@/types/generated/enums';
import type { ListAudienceSignalsParams } from '@/repository/audience-signals';
import type { ListNotificationsParams } from '@/repository/notifications';
import type { GoogleKeywordIdeaSeeds } from '@/repository/platform';
import type { NotificationLane, Platform } from '@/types/generated';
import type { DateRangeParams } from '@/lib/date-range';

export const AUTH_QUERY_KEYS = {
  me: ['auth', 'me'] as const,
  invitationPrefill: (token: string) =>
    ['auth', 'invitation-prefill', token] as const,
  socialProviders: ['auth', 'social-providers'] as const,
  signupKinds: ['auth', 'signup-kinds'] as const,
} as const;

export const DASHBOARD_QUERY_KEYS = {
  strategies: (params: GetStrategiesParams) =>
    ['dashboard', 'strategies', params] as const,
  performance: (params: GetPerformanceParams) =>
    ['dashboard', 'performance', params] as const,
  changeEventsBase: ['dashboard', 'change-events'] as const,
  changeEvents: (params: GetChangeEventsParams) =>
    ['dashboard', 'change-events', params] as const,
  workspaceSummary: (params: DateRangeParams) =>
    ['dashboard', 'workspace-summary', params] as const,
} as const;

export const STRATEGY_QUERY_KEYS = {
  root: ['strategies'] as const,
  listBase: ['strategies', 'list'] as const,
  list: (params: ListStrategiesParams) =>
    ['strategies', 'list', params] as const,
  listKpis: (params: ListKpisParams) =>
    ['strategies', 'list', 'kpis', params] as const,
  detail: (strategyId: number) => ['strategies', strategyId, 'detail'] as const,
  campaigns: (strategyId: number) =>
    ['strategies', strategyId, 'campaigns'] as const,
  campaign: (strategyId: number, campaignId: number) =>
    ['strategies', strategyId, 'campaigns', campaignId] as const,
  campaignAssets: (strategyId: number, campaignId: number) =>
    ['strategies', strategyId, 'campaigns', campaignId, 'assets'] as const,
  trend: (strategyId: number, params: StrategyTrendParams) =>
    ['strategies', strategyId, 'trend', params] as const,
  forecast: (strategyId: number, horizon: number) =>
    ['strategies', strategyId, 'forecast', horizon] as const,
  forecastAccuracy: (strategyId: number) =>
    ['strategies', strategyId, 'forecast', 'accuracy'] as const,
  generation: (strategyId: number) =>
    ['strategies', strategyId, 'generation'] as const,
  unmappedGoals: (strategyId: number) =>
    ['strategies', strategyId, 'unmapped-goals'] as const,
  // A mutation key, so every status control on the page sees one change in flight.
  statusUpdate: (strategyId: number) =>
    ['strategies', strategyId, 'status-update'] as const,
} as const;

export const AUDIENCE_QUERY_KEYS = {
  root: ['audiences'] as const,
  listBase: ['audiences', 'list'] as const,
  list: (params: ListAudiencesParams) => ['audiences', 'list', params] as const,
  detail: (audienceId: string) => ['audiences', audienceId, 'detail'] as const,
  searchBase: ['audiences', 'search'] as const,
  search: (params: Pick<ListAudiencesParams, 'search' | 'limit'>) =>
    ['audiences', 'search', params] as const,
} as const;

export const NOTIFICATIONS_QUERY_KEYS = {
  root: ['notifications'] as const,
  list: (params: ListNotificationsParams) =>
    ['notifications', 'list', params] as const,
  infiniteList: (params: ListNotificationsParams) =>
    ['notifications', 'infinite-list', params] as const,
  count: (lane?: NotificationLane) => ['notifications', 'count', lane] as const,
  preferences: ['notifications', 'preferences'] as const,
} as const;

export const AUDIENCE_SIGNAL_QUERY_KEYS = {
  list: (params: ListAudienceSignalsParams) =>
    ['audience-signals', 'list', params] as const,
} as const;

const CONVERSION_GOALS = 'conversion-goals';

export const CONVERSION_GOAL_QUERY_KEYS = {
  root: [CONVERSION_GOALS] as const,
  list: ['conversion-goals', 'list'] as const,
  eligible: (shape: EligibleGoalShape) =>
    [
      CONVERSION_GOALS,
      'eligible',
      shape.platform,
      shape.campaign_type,
      shape.lead_conversion_location,
      shape.family,
    ] as const,
  detail: (goalId: number) => ['conversion-goals', goalId, 'detail'] as const,
  mappings: (goalId: number) =>
    ['conversion-goals', goalId, 'platform-mappings'] as const,
  // Keyed on the action too: a republished goal maps to a new action with different code.
  googleTag: (goalId: number, actionId: string) =>
    [CONVERSION_GOALS, goalId, 'google-tag', actionId] as const,
} as const;

export {
  CUSTOM_REPORTS_QUERY_KEYS,
  NATIVE_REPORTS_QUERY_KEYS,
  PLATFORM_LINKS_QUERY_KEYS,
  REPORTS_QUERY_KEYS,
  SAVED_REPORTS_QUERY_KEYS,
} from '@/constants/report-query-keys';

export const BILLING_QUERY_KEYS = {
  summary: ['billing', 'summary'] as const,
  ledgerBase: ['billing', 'ledger'] as const,
  ledger: (params: ListCreditLedgerParams) =>
    ['billing', 'ledger', params] as const,
  trialOffer: ['billing', 'trial-offer'] as const,
  // Deliberately not a sub-key of `summary` — the billing-return poll must not be caught by
  // `invalidateQueries({ queryKey: summary })`'s prefix match, and must not share `summary`'s
  // cache entry (and its dataUpdatedAt/dataUpdateCount bookkeeping) with any other observer.
  summaryPoll: ['billing', 'summary-poll'] as const,
} as const;

export const RECOMMENDATIONS_QUERY_KEYS = {
  root: ['recommendations'] as const,
  list: ['recommendations', 'list'] as const,
  nextStep: ['recommendations', 'next-step'] as const,
  detail: (recommendationId: number) =>
    ['recommendations', recommendationId, 'detail'] as const,
} as const;

export const INSIGHTS_QUERY_KEYS = {
  list: ['insights', 'list'] as const,
} as const;

export const WIZARD_QUERY_KEYS = {
  productCategoriesSearch: (q: string, limit: number) =>
    ['wizard', 'product-categories', 'search', q, limit] as const,
  organizationProfile: ['wizard', 'organization-profile'] as const,
  topCampaignsBase: ['wizard', 'top-campaigns'] as const,
  topCampaignsByIntent: (days: number) =>
    ['wizard', 'top-campaigns', 'by-intent', days] as const,
  briefSuggestions: ['wizard', 'brief-suggestions'] as const,
  pinEligibility: (objective: string) =>
    ['wizard', 'pin-eligibility', objective] as const,
  draftBase: ['wizard', 'draft'] as const,
  draft: (strategyId: number) => ['wizard', 'draft', strategyId] as const,
  validateUrls: (strategyId: number) =>
    ['wizard', 'validate-urls', strategyId] as const,
  attention: (strategyId: number) =>
    ['wizard', 'attention', strategyId] as const,
  searchTermNarrative: (strategyId: number) =>
    ['wizard', 'search-term-narrative', strategyId] as const,
  metaCustomAudiences: (q: string, limit: number) =>
    ['wizard', 'meta', 'custom-audiences', q, limit] as const,
  googleGeoTargets: (q: string) =>
    ['wizard', 'google', 'geo-targets', q] as const,
  googleLanguages: (language: string) =>
    ['wizard', 'google', 'languages', language] as const,
  googleProductCategories: (q: string, language: string) =>
    ['wizard', 'google', 'product-categories', q, language] as const,
  googleProductCategoryNames: (ids: readonly number[], language: string) =>
    ['wizard', 'google', 'product-category-names', ids, language] as const,
  metaCustomAudienceLabels: (ids: readonly string[]) =>
    ['wizard', 'meta', 'custom-audience-labels', ids] as const,
  googleAccountAudiences: (q: string, limit: number) =>
    ['wizard', 'google', 'account-audiences', q, limit] as const,
  googleResourceLabels: (
    resourceNames: readonly string[],
    userListNames: readonly string[],
  ) =>
    [
      'wizard',
      'google',
      'audience-labels',
      resourceNames,
      userListNames,
    ] as const,
  googleNegativeKeywordLists: [
    'wizard',
    'google',
    'negative-keyword-lists',
  ] as const,
  googleBiddingStrategies: ['wizard', 'google', 'bidding-strategies'] as const,
  googleKeywordIdeas: (seeds: GoogleKeywordIdeaSeeds) =>
    ['wizard', 'google', 'keyword-ideas', seeds] as const,
} as const;

export const ORGANIZATION_QUERY_KEYS = {
  regions: ['organization', 'regions'] as const,
  setupStatus: ['organization', 'setup-status'] as const,
  detail: (organizationId: number) =>
    ['organizations', organizationId] as const,
  managementRequests: (organizationId: number) =>
    ['organizations', organizationId, 'management-requests'] as const,
  reportLogo: (organizationId: number) =>
    ['organizations', organizationId, 'report-logo'] as const,
} as const;

export const PLATFORM_CONNECTIONS_QUERY_KEYS = {
  list: ['platform-connections', 'list'] as const,
  googleAccountCallouts: [
    'platform-connections',
    'google',
    'account-callouts',
  ] as const,
} as const;

export const HEALTH_CHECK_QUERY_KEYS = {
  report: (platform: Platform) => ['health-check', 'report', platform] as const,
} as const;

export const GA4_EVENT_QUERY_KEYS = {
  rules: ['ga4-events', 'rules'] as const,
  discovery: ['ga4-events', 'discovery'] as const,
} as const;

export const COPILOT_QUERY_KEYS = {
  threads: ['copilot', 'threads'] as const,
  threadBase: ['copilot', 'thread'] as const,
  thread: (threadId: number) => ['copilot', 'thread', threadId] as const,
  mentions: (query: string) => ['copilot', 'mentions', query] as const,
  mentionResolve: (key: string) => ['copilot', 'mention-resolve', key] as const,
  suggestions: (contextType: string, step?: string) =>
    ['copilot', 'suggestions', contextType, step ?? null] as const,
} as const;

export const AGENCY_QUERY_KEYS = {
  assigneesAll: ['agency', 'assignees'] as const,
  assignees: (clientId: number) => ['agency', 'assignees', clientId] as const,
  assignments: ['agency', 'assignments'] as const,
  grants: ['agency', 'grants'] as const,
  onboardingAccounts: (platform: string) =>
    ['agency', 'onboarding-accounts', platform] as const,
  usage: ['agency', 'usage'] as const,
  portfolioAll: ['agency', 'portfolio'] as const,
  portfolio: (params: Record<string, unknown>) =>
    ['agency', 'portfolio', params] as const,
  attention: ['agency', 'attention'] as const,
} as const;

export const TEAM_QUERY_KEYS = {
  members: (organizationId: number) =>
    ['team', 'members', organizationId] as const,
  agencyStaff: (organizationId: number) =>
    ['team', 'agency-staff', organizationId] as const,
  invitations: ['team', 'invitations'] as const,
} as const;

export const SUPPORT_QUERY_KEYS = {
  all: ['support', 'requests'] as const,
  list: (params?: Record<string, unknown>) =>
    ['support', 'requests', 'list', params] as const,
  detail: (id: number) => ['support', 'requests', 'detail', id] as const,
} as const;

export const ASSET_QUERY_KEYS = {
  specs: ['assets', 'specs'] as const,
  listBase: ['assets', 'list'] as const,
  list: (params?: Record<string, unknown>) =>
    ['assets', 'list', params] as const,
  detail: (assetId: number) => ['assets', assetId, 'detail'] as const,
  blobUrl: (assetId: number) => ['assets', assetId, 'blob-url'] as const,
  generation: (jobId: string) => ['assets', 'generations', jobId] as const,
  generationList: ['assets', 'generations', 'recent'] as const,
} as const;

export const CAMPAIGN_QUERY_KEYS = {
  // Matches every business-model variant below, for a feed/page link that changes eligibility.
  capabilitiesBase: ['campaigns', 'capabilities'] as const,
  capabilities: (businessModel: BusinessModel) =>
    ['campaigns', 'capabilities', businessModel] as const,
} as const;

const AI_PERFORMANCE = 'ai-performance';

export const AI_PERFORMANCE_QUERY_KEYS = {
  overview: (params: AiPerformanceParams) =>
    [AI_PERFORMANCE, 'overview', params] as const,
  aiAnomalies: (params: AiPerformanceParams) =>
    [AI_PERFORMANCE, 'ai-anomalies', params] as const,
  copilotHelpfulness: (params: AiPerformanceParams) =>
    [AI_PERFORMANCE, 'copilot-helpfulness', params] as const,
} as const;
